import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Calendar, Clock, Users, Trophy, Zap, ChevronRight, Search, ArrowUpDown, Sparkles, ShieldCheck, Medal, Crown, User, Swords, Target, Award, Loader2 } from 'lucide-react';
import axios from 'axios';
import { API } from '@/api';
import { useLang } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { createTextPicker, getLocalizedField, pickLocalizedText } from '../utils/localizedContent';

const TCG_COLORS = { pokemon: '#D4A84B', magic: '#0D5E4A', yugioh: '#C45B4A', onepiece: '#EF4444', riftbound: '#0BC4E2', board_game: '#2D8B70', all: '#C9A962', dbs: '#C45B4A' };
const TCG_META = {
  pokemon: { label: 'Pokémon', color: '#D4A84B' },
  magic: { label: 'Magic', color: '#0D5E4A' },
  yugioh: { label: 'Yu-Gi-Oh!', color: '#C45B4A' },
  onepiece: { label: 'One Piece', color: '#EF4444' },
  riftbound: { label: 'Riftbound', color: '#0BC4E2' },
  board_game: { label: 'Board Game', labelEn: 'Board Game', color: '#2D8B70' },
};

export default function Tournaments() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialView = searchParams.get('view') === 'ranking' ? 'ranking' : 'tournaments';
  const [activeView, setActiveView] = useState(initialView);

  const [tournaments, setTournaments] = useState([]);
  const [tournamentsLoading, setTournamentsLoading] = useState(true);
  const [filterTcg, setFilterTcg] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortMode, setSortMode] = useState('date_asc');
  const [leaderboard, setLeaderboard] = useState([]);
  const [tcgTabs, setTcgTabs] = useState([]);
  const [activeLeaderboardTab, setActiveLeaderboardTab] = useState('total');
  const [leaderboardLoading, setLeaderboardLoading] = useState(true);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [playerStats, setPlayerStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const { t, lang } = useLang();

  const l = createTextPicker(lang);
  const lp = (...args) => pickLocalizedText(lang, ...args);

  const fetchTournaments = useCallback(async () => {
    setTournamentsLoading(true);
    try {
      const params = {
        event_type: 'tournament',
        // Estado vacío desactiva el filtro por defecto "upcoming" y devuelve todos los estados de torneo.
        status: filterStatus === 'all' ? '' : filterStatus,
      };
      if (filterTcg) params.tcg_type = filterTcg;
      const { data } = await axios.get(`${API}/events`, { params });
      setTournaments(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch tournaments:', err);
      setTournaments([]);
    } finally {
      setTournamentsLoading(false);
    }
  }, [filterTcg, filterStatus]);

  useEffect(() => {
    fetchTournaments();
  }, [fetchTournaments]);

  const fetchLeaderboard = useCallback(async () => {
    setLeaderboardLoading(true);
    try {
      const params = activeLeaderboardTab !== 'total' ? { tcg_type: activeLeaderboardTab } : {};
      const { data } = await axios.get(`${API}/events/stats/leaderboard`, { params });
      setLeaderboard(data.leaderboard || []);
      setTcgTabs((data.tcg_types || []).filter(Boolean));
    } catch (err) {
      console.error('Error fetching leaderboard:', err);
      setLeaderboard([]);
    } finally {
      setLeaderboardLoading(false);
    }
  }, [activeLeaderboardTab]);

  useEffect(() => {
    if (activeView === 'ranking') {
      fetchLeaderboard();
    }
  }, [activeView, fetchLeaderboard]);

  const fetchPlayerStats = async (userId) => {
    setStatsLoading(true);
    try {
      const { data } = await axios.get(`${API}/events/stats/player/${userId}`);
      setPlayerStats(data);
    } catch (err) {
      console.error('Error fetching player stats:', err);
      setPlayerStats(null);
    } finally {
      setStatsLoading(false);
    }
  };

  const handlePlayerClick = (player) => {
    setSelectedPlayer(player);
    fetchPlayerStats(player.user_id);
  };

  const setView = (view) => {
    setActiveView(view);
    const next = new URLSearchParams(searchParams);
    if (view === 'ranking') next.set('view', 'ranking');
    else next.delete('view');
    setSearchParams(next, { replace: true });
  };

  const tcgFilters = [
    { key: '', label: l('Todos', 'All') },
    { key: 'pokemon', label: 'Pokémon' },
    { key: 'magic', label: 'Magic' },
    { key: 'yugioh', label: 'Yu-Gi-Oh!' },
    { key: 'onepiece', label: 'One Piece' },
    { key: 'riftbound', label: 'Riftbound' },
    { key: 'board_game', label: l('Juegos de Mesa', 'Board Games') },
  ];
  const statusFilters = [
    { key: 'all', label: l('Todos', 'All') },
    { key: 'upcoming', label: l('Próximos', 'Upcoming') },
    { key: 'in_progress', label: l('En curso', 'In progress') },
    { key: 'completed', label: l('Finalizados', 'Completed') },
  ];

  const getPrizeDetails = (event) => {
    const details = [];
    const customPrizes = Array.isArray(event.custom_prizes) ? event.custom_prizes : [];
    const championPrize = customPrizes.find((p) => p.position === 1) || customPrizes[0];
    const prizeText = getLocalizedField(event, 'prize', lang, event.prize);

    if (event.prize) {
      details.push({ key: 'text', label: l('Premio', 'Prize'), value: prizeText });
    }
    if (championPrize?.voucher_value > 0) {
      details.push({
        key: 'voucher',
        label: l('Vale', 'Voucher'),
        value: `${championPrize.voucher_value.toFixed(2)}€`,
      });
    }
    if (championPrize?.loyalty_points > 0) {
      details.push({
        key: 'points',
        label: l('Puntos', 'Points'),
        value: `${championPrize.loyalty_points} pts`,
      });
    }
    if (championPrize?.physical_prize || championPrize?.physical_prize_es) {
      details.push({
        key: 'physical',
        label: l('Premio físico', 'Physical prize'),
        value: getLocalizedField(championPrize, 'physical_prize', lang, championPrize.physical_prize),
      });
    }
    return details;
  };

  const getPositionPrizeSummary = (prize) => {
    const segments = [];
    if (prize.voucher_value > 0) segments.push(`${prize.voucher_value.toFixed(2)}€`);
    if (prize.loyalty_points > 0) segments.push(`${prize.loyalty_points} pts`);
    const physical = getLocalizedField(prize, 'physical_prize', lang, prize.physical_prize);
    if (physical) segments.push(physical);
    return segments.join(' + ');
  };

  const getStatusMeta = (event) => {
    const participants = event.registered_participants?.length || 0;
    const max = event.max_participants || 0;
    const isFull = max > 0 && participants >= max;
    const status = event.status || 'upcoming';
    if (status === 'in_progress') {
      return {
        label: l('En curso', 'In progress'),
        className: 'bg-[#0D5E4A]/20 text-[#6EE7B7] border border-[#0D5E4A]/30',
      };
    }
    if (status === 'completed') {
      return {
        label: l('Finalizado', 'Completed'),
        className: 'bg-[#A8B5AE]/20 text-[#D1D5DB] border border-[#A8B5AE]/30',
      };
    }
    if (isFull) {
      return {
        label: l('Completo', 'Full'),
        className: 'bg-[#C45B4A]/20 text-[#FCA5A5] border border-[#C45B4A]/30',
      };
    }
    return {
      label: l('Abierto', 'Open'),
      className: 'bg-[#2D8B70]/20 text-[#86EFAC] border border-[#2D8B70]/30',
    };
  };

  const parseEventDate = (event) => {
    const raw = `${event.date || ''}T${event.time || '00:00'}:00`;
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
  };

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const visibleTournaments = tournaments
    .filter((event) => {
      if (!normalizedSearch) return true;
      const haystack = [
        event.title,
        event.title_es,
        event.description,
        event.description_es,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(normalizedSearch);
    })
    .sort((a, b) => {
      if (sortMode === 'players_desc') {
        return (b.registered_participants?.length || 0) - (a.registered_participants?.length || 0);
      }
      return parseEventDate(a) - parseEventDate(b);
    });

  const getVisiblePoints = (player) => {
    if (!player) return 0;
    if (activeLeaderboardTab === 'total') return player.total_points ?? player.points ?? 0;
    return player.points_by_tcg?.[activeLeaderboardTab] || 0;
  };

  const getRankIcon = (index) => {
    if (index === 0) return <Crown size={20} className="text-[#FFD700]" />;
    if (index === 1) return <Medal size={20} className="text-[#C0C0C0]" />;
    if (index === 2) return <Medal size={20} className="text-[#CD7F32]" />;
    return <span className="text-[#A8B5AE] font-mono text-sm">#{index + 1}</span>;
  };

  const getRankBg = (index) => {
    if (index === 0) return 'bg-gradient-to-r from-[#FFD700]/20 to-transparent border-[#FFD700]/30';
    if (index === 1) return 'bg-gradient-to-r from-[#C0C0C0]/20 to-transparent border-[#C0C0C0]/30';
    if (index === 2) return 'bg-gradient-to-r from-[#CD7F32]/20 to-transparent border-[#CD7F32]/30';
    return 'bg-card border-border';
  };

  const filteredPointsByTcg = useMemo(() => {
    if (!playerStats?.points_by_tcg) return [];
    return Object.entries(playerStats.points_by_tcg);
  }, [playerStats]);

  return (
    <div className="max-w-7xl mx-auto px-6 py-8 page-enter">
      <section className="relative overflow-hidden rounded-3xl border border-border bg-gradient-to-br from-card via-card to-muted/60 p-6 md:p-8 mb-6">
        <div className="absolute -top-20 -right-16 w-56 h-56 rounded-full bg-[#C9A962]/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-20 -left-12 w-52 h-52 rounded-full bg-[#2D8B70]/10 blur-3xl pointer-events-none" />
        <div className="relative z-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#C9A962]/10 border border-[#C9A962]/30 text-[#C9A962] text-xs font-semibold mb-3">
            <Sparkles size={12} />
            {l('Circuito competitivo', 'Competitive circuit')}
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-foreground mb-2" style={{ fontFamily: 'Cinzel, serif' }}>
            {t('events.tournaments_title')}
          </h1>
          <p className="text-muted-foreground mb-5 max-w-2xl">{t('events.tournaments_subtitle')}</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-card/80 border border-border rounded-xl px-4 py-3">
              <p className="text-[#8FA59B] text-xs uppercase tracking-wide">{l('Torneos visibles', 'Visible tournaments')}</p>
              <p className="text-foreground text-2xl font-black">{visibleTournaments.length}</p>
            </div>
            <div className="bg-card/80 border border-border rounded-xl px-4 py-3">
              <p className="text-[#8FA59B] text-xs uppercase tracking-wide">{l('TCG filtrado', 'Filtered TCG')}</p>
              <p className="text-foreground text-sm font-semibold">{tcgFilters.find((f) => f.key === filterTcg)?.label || l('Todos', 'All')}</p>
            </div>
            <div className="bg-card/80 border border-border rounded-xl px-4 py-3">
              <p className="text-[#8FA59B] text-xs uppercase tracking-wide">{l('Modo', 'Mode')}</p>
              <p className="text-foreground text-sm font-semibold inline-flex items-center gap-1">
                <ShieldCheck size={14} className="text-[#C9A962]" />
                {l('Ranking activo', 'Live rankings')}
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="inline-flex items-center rounded-2xl border border-border bg-card p-1 mb-6">
        <button
          onClick={() => setView('tournaments')}
          className={`px-4 py-2 rounded-xl text-sm font-semibold ${activeView === 'tournaments' ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:text-foreground'}`}
          data-testid="selector-tournaments"
        >
          {t('nav.tournaments')}
        </button>
        <button
          onClick={() => setView('ranking')}
          className={`px-4 py-2 rounded-xl text-sm font-semibold ${activeView === 'ranking' ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:text-foreground'}`}
          data-testid="selector-ranking"
        >
          {t('nav.ranking')}
        </button>
      </div>

      {activeView === 'tournaments' && (
      <>
      <div className="flex flex-wrap gap-2 mb-4">
        {tcgFilters.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilterTcg(f.key)}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${filterTcg === f.key ? 'bg-primary/20 text-primary' : 'bg-card border border-border text-muted-foreground hover:text-foreground'}`}
            data-testid={`filter-tcg-${f.key || 'all'}`}
          >
            {f.label}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        {statusFilters.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilterStatus(f.key)}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${filterStatus === f.key ? 'bg-primary/20 text-primary' : 'bg-card border border-border text-muted-foreground hover:text-foreground'}`}
            data-testid={`filter-status-${f.key}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={l('Buscar torneo...', 'Search tournament...')}
            className="w-full bg-card border border-border rounded-xl pl-9 pr-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50"
          />
        </div>
        <button
          onClick={() => setSortMode((s) => (s === 'date_asc' ? 'players_desc' : 'date_asc'))}
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold bg-card border border-border text-muted-foreground hover:text-foreground"
        >
          <ArrowUpDown size={14} />
          {sortMode === 'date_asc'
            ? l('Orden: Fecha', 'Sort: Date')
            : l('Orden: Participantes', 'Sort: Participants')}
        </button>
      </div>

      {tournamentsLoading ? (
        <div className="space-y-4">
          {[...Array(4)].map((_, i) => (
            <div key={`tournament-skeleton-${i}`} className="card-glass h-36 skeleton"></div>
          ))}
        </div>
      ) : visibleTournaments.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground">
          <Trophy size={48} className="mx-auto mb-4 opacity-30" />
          <p>{normalizedSearch ? l('No hay torneos con esa búsqueda.', 'No tournaments match your search.') : t('events.empty_tournaments')}</p>
        </div>
      ) : (
        <div className="max-h-[68vh] overflow-y-auto pr-1 space-y-4">
          {visibleTournaments.map((event) => {
            const color = TCG_COLORS[event.tcg_type] || '#C9A962';
            const participants = event.registered_participants?.length || 0;
            const prizeDetails = getPrizeDetails(event);
            const statusMeta = getStatusMeta(event);
            const customPrizes = Array.isArray(event.custom_prizes) ? event.custom_prizes : [];
            const title = getLocalizedField(event, 'title', lang, event.title);
            const description = getLocalizedField(event, 'description', lang, event.description);
            const additionalPositionPrizes = customPrizes
              .filter((p) => p.position > 1)
              .sort((a, b) => a.position - b.position)
              .filter((p) => getPositionPrizeSummary(p));
            return (
              <Link
                key={event.event_id}
                to={`/events/${event.event_id}`}
                className="overflow-hidden flex flex-col md:flex-row rounded-2xl border border-border bg-gradient-to-br from-card to-muted/60 hover:border-primary/40 transition-all group hover:shadow-[0_10px_35px_rgba(0,0,0,0.18)]"
                data-testid={`tournament-${event.event_id}`}
              >
                {event.image && (
                  <div className="md:w-56 h-40 md:h-auto overflow-hidden relative">
                    <img src={event.image} alt={title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-card/90 to-transparent md:bg-gradient-to-r"></div>
                  </div>
                )}

                <div className="p-5 md:p-6 flex-1">
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 rounded-full text-xs font-bold" style={{ background: `${color}20`, color, border: `1px solid ${color}40` }}>
                        {t('events.tournament')}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${statusMeta.className}`}>
                        {statusMeta.label}
                      </span>
                    </div>
                    <ChevronRight size={16} className="text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>

                  <h3 className="text-foreground font-bold text-xl mb-1 group-hover:text-primary transition-colors" style={{ fontFamily: 'Cinzel, serif' }}>
                    {title}
                  </h3>
                  <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
                    {description}
                  </p>

                  <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground mb-3">
                    <span className="flex items-center gap-1"><Calendar size={12} />{event.date}</span>
                    <span className="flex items-center gap-1"><Clock size={12} />{event.time}</span>
                    <span className="flex items-center gap-1"><Users size={12} />{participants}/{event.max_participants}</span>
                  </div>

                  <div className="flex flex-wrap items-center gap-4 text-xs">
                    {prizeDetails.map((item) => (
                      <span key={item.key} className="flex items-center gap-2 text-muted-foreground">
                        <Trophy size={12} className="text-[#C9A962]" />
                        {item.label}: <span className="text-foreground">{item.value}</span>
                      </span>
                    ))}
                    {event.entry_fee > 0 && (
                      <span className="flex items-center gap-2 text-muted-foreground">
                        <Zap size={12} className="text-[#C9A962]" />
                        {t('events.entry_fee')}: <span className="text-[#C9A962] font-bold">{event.entry_fee?.toFixed(2)}€</span>
                      </span>
                    )}
                  </div>

                  {additionalPositionPrizes.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-[#C9A962]/15">
                      <p className="text-[11px] uppercase tracking-wide text-[#8B9A92] mb-1.5">
                        {l('Premios por posición', 'Position prizes')}
                      </p>
                      <div className="flex flex-wrap gap-2 text-xs">
                        {additionalPositionPrizes.map((prize) => (
                          <span key={`pos-prize-${event.event_id}-${prize.position}`} className="px-2 py-1 rounded-lg bg-card border border-primary/15 text-muted-foreground">
                            #{prize.position}: <span className="text-foreground">{getPositionPrizeSummary(prize)}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      )}
      </>
      )}

      {activeView === 'ranking' && (
        <>
          <div className="flex flex-wrap gap-2 mb-6 border-b border-border pb-3">
            <TabButton active={activeLeaderboardTab === 'total'} onClick={() => setActiveLeaderboardTab('total')} label={l('Totales', 'Totals')} />
            {tcgTabs.map((tcg) => {
              const meta = TCG_META[tcg] || { label: tcg, color: '#C9A962' };
              return (
                <TabButton
                  key={tcg}
                  active={activeLeaderboardTab === tcg}
                  onClick={() => setActiveLeaderboardTab(tcg)}
                  label={lp(meta.label, meta.labelEn || meta.label)}
                  color={meta.color}
                />
              );
            })}
          </div>
          <div className="mb-4 text-sm text-muted-foreground">
            {activeLeaderboardTab === 'total'
              ? l('Ranking global de todos los TCG.', 'Global ranking across all TCGs.')
              : l('Ranking del juego seleccionado.', 'Ranking for selected game.')}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              {leaderboardLoading ? (
                <div className="flex items-center justify-center py-20"><Loader2 size={32} className="animate-spin text-[#C9A962]" /></div>
              ) : leaderboard.length === 0 ? (
                <div className="text-center py-20 text-muted-foreground bg-card border border-border rounded-2xl">
                  <Trophy size={48} className="mx-auto mb-4 opacity-30" />
                  <p>{l('Aun no hay datos de torneos', 'No tournament data yet')}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {leaderboard.map((player, index) => (
                    <div
                      key={player.user_id}
                      onClick={() => handlePlayerClick(player)}
                      className={`flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition-all hover:scale-[1.01] ${getRankBg(index)} ${
                        selectedPlayer?.user_id === player.user_id ? 'ring-2 ring-[#C9A962]' : ''
                      } ${player.user_id === user?.user_id ? 'ring-1 ring-[#10B981]' : ''}`}
                    >
                      <div className="w-10 flex justify-center">{getRankIcon(index)}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-foreground font-bold truncate">{player.name}</p>
                          {player.championships > 0 && <span className="px-1.5 py-0.5 bg-[#C9A962]/20 text-[#C9A962] text-xs rounded font-semibold">{player.championships}x 🏆</span>}
                          {player.user_id === user?.user_id && <span className="px-1.5 py-0.5 bg-[#10B981]/20 text-[#10B981] text-xs rounded">{l('Tu', 'You')}</span>}
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                          <span>{player.tournaments_played} {l('torneos', 'tournaments')}</span>
                          <span>•</span>
                          <span className="text-[#10B981]">{player.wins}W</span>
                          <span className="text-[#EF4444]">{player.losses}L</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-[#C9A962] font-black text-xl">{getVisiblePoints(player)}</p>
                        <p className="text-muted-foreground text-xs">{l('puntos', 'points')}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="lg:col-span-1">
              {selectedPlayer ? (
                <div className="bg-card border border-border rounded-2xl p-6 sticky top-6">
                  {statsLoading ? (
                    <div className="flex items-center justify-center py-12"><Loader2 size={24} className="animate-spin text-[#C9A962]" /></div>
                  ) : playerStats ? (
                    <>
                      <div className="flex items-center gap-4 mb-6">
                        <div className="w-14 h-14 rounded-full bg-[#C9A962]/20 flex items-center justify-center"><User size={24} className="text-[#C9A962]" /></div>
                        <div>
                          <h3 className="text-foreground font-bold text-lg">{playerStats.name}</h3>
                          <p className="text-muted-foreground text-sm">{playerStats.tournaments_played} {l('torneos jugados', 'tournaments played')}</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3 mb-6">
                        <StatCard icon={Swords} value={playerStats.total_matches} label={l('Partidas', 'Matches')} valueClass="text-foreground" />
                        <StatCard icon={Target} value={`${playerStats.win_rate}%`} label={l('Victorias', 'Win Rate')} valueClass="text-[#10B981]" iconClass="text-[#10B981]" />
                        <StatCard icon={Trophy} value={playerStats.championships} label={l('Titulos', 'Titles')} valueClass="text-[#C9A962]" iconClass="text-[#C9A962]" />
                        <StatCard icon={Award} value={playerStats.total_points || 0} label={l('Puntos', 'Points')} valueClass="text-[#8B5CF6]" iconClass="text-[#8B5CF6]" />
                      </div>
                      {filteredPointsByTcg.length > 0 && (
                        <div className="mb-6">
                          <h4 className="text-[#C9A962] font-semibold text-sm mb-2">{l('Puntos por TCG', 'Points by TCG')}</h4>
                          <div className="space-y-2">
                            {filteredPointsByTcg.map(([tcg, pts]) => (
                              <div key={tcg} className="flex items-center justify-between bg-muted/50 rounded-lg px-3 py-2">
                                <span className="text-muted-foreground text-sm">{(TCG_META[tcg]?.label || tcg).toUpperCase()}</span>
                                <span className="text-foreground font-semibold text-sm">{pts}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  ) : null}
                </div>
              ) : (
                <div className="bg-card border border-border rounded-2xl p-6 text-center">
                  <User size={40} className="mx-auto text-muted-foreground/30 mb-3" />
                  <p className="text-muted-foreground text-sm">{l('Selecciona un jugador para ver sus estadisticas', 'Select a player to view their stats')}</p>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function TabButton({ active, onClick, label, color }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
        active ? 'bg-primary/20 text-primary border border-primary/30' : 'text-muted-foreground hover:text-foreground border border-border'
      }`}
      style={active && color ? { borderColor: color, color } : {}}
    >
      {label}
    </button>
  );
}

function StatCard({ icon: Icon, value, label, valueClass = 'text-foreground', iconClass = 'text-muted-foreground' }) {
  return (
    <div className="bg-[#162B24] rounded-xl p-3 text-center">
      <Icon size={18} className={`mx-auto mb-1 ${iconClass}`} />
      <p className={`font-bold text-xl ${valueClass}`}>{value}</p>
      <p className="text-muted-foreground text-xs">{label}</p>
    </div>
  );
}
